import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Container, Paper, Chip, Divider, keyframes } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PhotoFilterIcon from '@mui/icons-material/PhotoFilter';
import BoltIcon from '@mui/icons-material/Bolt';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import SunAnimation from './SunAnimation';

const authBtnShine = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const authBtnFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const authBtnFadeIn = keyframes`
  0% { opacity: 0; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const borderRotate = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`;

const flipIn = keyframes`
  0% {
    opacity: 0;
    transform: rotateX(90deg);
  }
  100% {
    opacity: 1;
    transform: rotateX(0deg);
  }
`;

// Big-brand aurora: slow, organic drift with rotation and scale breathing
const auroraDriftA = keyframes`
  0%   { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
  33%  { transform: translate3d(-2.5%, -1.5%, 0) rotate(8deg) scale(1.06); }
  66%  { transform: translate3d(2%, 2%, 0) rotate(-4deg) scale(0.97); }
  100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
`;

const auroraDriftB = keyframes`
  0%   { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); opacity: 0.55; }
  50%  { transform: translate3d(3%, 3.5%, 0) rotate(-10deg) scale(1.08); opacity: 0.78; }
  100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); opacity: 0.55; }
`;

const auroraDriftC = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.6; }
  50%      { transform: translate3d(-3%, -4%, 0) scale(1.12); opacity: 0.85; }
`;

const auroraDriftD = keyframes`
  0%   { transform: translate3d(0, 0, 0) rotate(0deg); }
  50%  { transform: translate3d(4%, -2.5%, 0) rotate(12deg); }
  100% { transform: translate3d(0, 0, 0) rotate(0deg); }
`;

const gridPan = keyframes`
  0%   { background-position: 0 0; }
  100% { background-position: 48px 48px; }
`;

// 轨道圆环缓慢旋转 —— 给背景添加「建构感」
const orbitRotateSlow = keyframes`
  0%   { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
`;

const orbitRotateReverse = keyframes`
  0%   { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(-360deg); }
`;

// 星火闪烁 —— 品牌点缀
const sparkleTwinkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.6); }
  50%      { opacity: 1;   transform: scale(1); }
`;

// 雷达式光环外扩 —— 带节奏的「能量感」
const radarPulse = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0; }
  20%  { opacity: 0.55; }
  100% { transform: translate(-50%, -50%) scale(1.8); opacity: 0; }
`;

// 向下滚动指示器 —— 弹跳
const scrollBounce = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.9; }
  50%      { transform: translateY(6px); opacity: 1; }
`;

const scrollGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.45), 0 10px 24px rgba(37, 99, 235, 0.18); }
  50%      { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0),    0 14px 28px rgba(37, 99, 235, 0.28); }
`;

// 编辑风衬线字体栈（与 AboutPage 一致）
const SERIF_STACK =
  '"Iowan Old Style", "Apple Garamond", "Baskerving", "Times New Roman", "Songti SC", "宋体", Georgia, serif';

type Segment = { text: string; accent?: boolean };

const TypewriterText = ({
  segments,
  text,
}: {
  segments?: Segment[];
  text?: string;
}) => {
  // 兼容旧的 text 用法，新的优先用 segments
  const parts: Segment[] = segments ?? (text ? [{ text }] : []);

  let globalIndex = 0;
  return (
    <Typography
      component="h1"
      sx={{
        mb: 2.5,
        fontSize: { xs: '2rem', md: '3.8rem' },
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
        display: 'inline-block',
      }}
    >
      {parts.map((seg, segIdx) =>
        seg.text.split('').map((char) => {
          const i = globalIndex++;
          return (
            <Box
              component="span"
              key={`${segIdx}-${i}`}
              sx={{
                display: 'inline-block',
                opacity: 0,
                transformOrigin: 'bottom',
                animation: `${flipIn} 1s cubic-bezier(0.5, 0, 0.5, 1) forwards`,
                animationDelay: `${i * 0.12}s`,
                minWidth: char === ' ' ? '0.4em' : 'auto',
                ...(seg.accent
                  ? {
                      // 强调段：衬线 italic + 品牌蓝
                      fontFamily: SERIF_STACK,
                      fontStyle: 'italic',
                      fontWeight: 500,
                      color: '#2563eb',
                    }
                  : {
                      // 主段：深墨色，加粗但不渐变
                      fontWeight: 800,
                      color: '#0a0a0a',
                    }),
              }}
            >
              {char === ' ' ? '\u00A0' : char}
            </Box>
          );
        })
      )}
    </Typography>
  );
};


const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg,#f8fbff 0%,#f3f7fd 40%,#ffffff 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* SEO: 隐藏的语义化标题，对搜索引擎暴露关键词，不影响视觉 */}
      <Box
        component="h1"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
          fontSize: 0,
        }}
      >
        Xup · 让 AI 走进普通人的生活 · Sora 2 / Veo 3 / Grok Imagine / Nano Banana 2 一站式 AI 视频与图像生成平台
      </Box>
      <Box
        component="h2"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
          fontSize: 0,
        }}
      >
        国内直接使用 Sora 2 文生视频、Veo 3 Pro 高清视频生成、Grok Imagine 30s 长镜头、Nano Banana 2 高清图像生成。无需翻墙、按次付费、积分永不过期。
      </Box>
      {/* ═════ Premium Aurora Background · 大厂级背景系统 ═════ */}
      {/* 固定全屏容器：所有背景层都锚定在视口，避免跟随页面滚动导致的视觉混乱 */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          // 底层基色渐变——冷白到淡蔚蓝的大面积底调
          background:
            'radial-gradient(120% 80% at 80% 10%, #eef4ff 0%, #f7faff 45%, #ffffff 100%)',
        }}
      >
        {/* Aurora A · 右上大面积湖蓝光晕（旋转呼吸 34s） */}
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: '80vw',
            height: '80vw',
            maxWidth: 1400,
            maxHeight: 1400,
            borderRadius: '50%',
            background:
              'radial-gradient(closest-side, rgba(129, 170, 255, 0.55) 0%, rgba(147, 197, 253, 0.42) 28%, rgba(147, 197, 253, 0.12) 55%, rgba(147, 197, 253, 0) 75%)',
            filter: 'blur(70px)',
            willChange: 'transform',
            animation: `${auroraDriftA} 34s cubic-bezier(0.45, 0.02, 0.5, 0.98) infinite`,
          }}
        />

        {/* Aurora B · 中心偏上紫蓝调晕染（12s 慢呼吸） */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '30%',
            width: '70vw',
            height: '70vw',
            maxWidth: 1100,
            maxHeight: 1100,
            borderRadius: '50%',
            background:
              'radial-gradient(closest-side, rgba(167, 139, 250, 0.28) 0%, rgba(147, 197, 253, 0.32) 35%, rgba(191, 219, 254, 0.12) 60%, rgba(219, 234, 254, 0) 78%)',
            filter: 'blur(80px)',
            willChange: 'transform, opacity',
            animation: `${auroraDriftB} 42s cubic-bezier(0.45, 0.02, 0.5, 0.98) infinite`,
            animationDelay: '-6s',
          }}
        />

        {/* Aurora C · 右下冷蔚凸显（52s 慢流动） */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-15%',
            right: '10%',
            width: '55vw',
            height: '55vw',
            maxWidth: 900,
            maxHeight: 900,
            borderRadius: '50%',
            background:
              'radial-gradient(closest-side, rgba(96, 165, 250, 0.42) 0%, rgba(125, 211, 252, 0.3) 35%, rgba(147, 197, 253, 0.08) 62%, rgba(147, 197, 253, 0) 80%)',
            filter: 'blur(90px)',
            willChange: 'transform, opacity',
            animation: `${auroraDriftC} 52s cubic-bezier(0.45, 0.02, 0.5, 0.98) infinite`,
            animationDelay: '-18s',
          }}
        />

        {/* Aurora D · 中心微粉色调（28s 温柔偏移） */}
        <Box
          sx={{
            position: 'absolute',
            top: '38%',
            left: '38%',
            width: '42vw',
            height: '42vw',
            maxWidth: 720,
            maxHeight: 720,
            borderRadius: '50%',
            background:
              'radial-gradient(closest-side, rgba(244, 114, 182, 0.12) 0%, rgba(167, 139, 250, 0.14) 38%, rgba(147, 197, 253, 0.06) 64%, rgba(147, 197, 253, 0) 82%)',
            filter: 'blur(70px)',
            willChange: 'transform',
            animation: `${auroraDriftD} 28s cubic-bezier(0.45, 0.02, 0.5, 0.98) infinite`,
            animationDelay: '-9s',
          }}
        />

        {/* 全屏点阵网格 · 极低透明度 · 径向蒙版向中心淡出 */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(30, 64, 175, 0.12) 0.8px, transparent 0.8px)',
            backgroundSize: '28px 28px',
            WebkitMaskImage:
              'radial-gradient(ellipse 70% 65% at 50% 40%, #000 0%, rgba(0,0,0,0.7) 45%, transparent 85%)',
            maskImage:
              'radial-gradient(ellipse 70% 65% at 50% 40%, #000 0%, rgba(0,0,0,0.7) 45%, transparent 85%)',
            animation: `${gridPan} 60s linear infinite`,
            opacity: 0.55,
            mixBlendMode: 'multiply',
          }}
        />

        {/* 轨道圆环 · 大圆极慢顺时针 · 给 AI 主题注入「建构感」 */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '140vmin',
            height: '140vmin',
            borderRadius: '50%',
            border: '1px dashed rgba(59, 130, 246, 0.14)',
            animation: `${orbitRotateSlow} 180s linear infinite`,
            WebkitMaskImage:
              'conic-gradient(from 0deg, transparent 0deg, #000 70deg, #000 180deg, transparent 240deg, transparent 360deg)',
            maskImage:
              'conic-gradient(from 0deg, transparent 0deg, #000 70deg, #000 180deg, transparent 240deg, transparent 360deg)',
          }}
        />

        {/* 中圆环 · 逆时针 · 错位增强空间感 */}
        <Box
          sx={{
            position: 'absolute',
            top: '46%',
            left: '52%',
            width: '85vmin',
            height: '85vmin',
            borderRadius: '50%',
            border: '1px solid rgba(99, 102, 241, 0.11)',
            animation: `${orbitRotateReverse} 140s linear infinite`,
            WebkitMaskImage:
              'conic-gradient(from 180deg, transparent 0deg, #000 60deg, #000 200deg, transparent 260deg, transparent 360deg)',
            maskImage:
              'conic-gradient(from 180deg, transparent 0deg, #000 60deg, #000 200deg, transparent 260deg, transparent 360deg)',
          }}
        />

        {/* 雷达脉冲 · 中心点能量外扩，每 10s 一次 */}
        <Box
          sx={{
            position: 'absolute',
            top: '48%',
            left: '50%',
            width: '52vmin',
            height: '52vmin',
            borderRadius: '50%',
            border: '1.5px solid rgba(59, 130, 246, 0.28)',
            animation: `${radarPulse} 10s cubic-bezier(0.22, 0.8, 0.3, 1) infinite`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '48%',
            left: '50%',
            width: '52vmin',
            height: '52vmin',
            borderRadius: '50%',
            border: '1.5px solid rgba(167, 139, 250, 0.22)',
            animation: `${radarPulse} 10s cubic-bezier(0.22, 0.8, 0.3, 1) infinite`,
            animationDelay: '-4s',
          }}
        />

        {/* 星火点缀 · 6 颗 · 错时闪烁 */}
        {[
          { top: '14%', left: '18%', size: 4, delay: '0s',   dur: '3.8s', color: '#60a5fa' },
          { top: '22%', left: '78%', size: 5, delay: '1.2s', dur: '4.4s', color: '#a78bfa' },
          { top: '62%', left: '12%', size: 3, delay: '0.6s', dur: '3.2s', color: '#38bdf8' },
          { top: '72%', left: '82%', size: 4, delay: '2.1s', dur: '4s',   color: '#818cf8' },
          { top: '38%', left: '88%', size: 3, delay: '1.6s', dur: '3.6s', color: '#60a5fa' },
          { top: '82%', left: '48%', size: 5, delay: '0.3s', dur: '4.2s', color: '#a78bfa' },
        ].map((s, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              top: s.top,
              left: s.left,
              width: s.size,
              height: s.size,
              borderRadius: '50%',
              bgcolor: s.color,
              boxShadow: `0 0 ${s.size * 3}px ${s.color}`,
              animation: `${sparkleTwinkle} ${s.dur} ease-in-out infinite`,
              animationDelay: s.delay,
            }}
          />
        ))}

        {/* Grain 噪点 · SVG feTurbulence · 给纯色大面积补充质感 */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.06 0 0 0 0 0.1 0 0 0 0 0.2 0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            opacity: 0.06,
            mixBlendMode: 'multiply',
            pointerEvents: 'none',
          }}
        />

        {/* 底部渐隐白幕 · 平滑过渡到精选能力卡片区 */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '35%',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 55%, rgba(255,255,255,0.85) 100%)',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Navigation */}
      <Box
        component="nav"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: { xs: '20px 20px', md: '24px 40px' },
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              color: '#000000',
              letterSpacing: '2px',
              fontSize: { xs: '20px', md: '24px' },
              fontFamily: '"Orbitron", sans-serif',
            }}
          >
            Xup
          </Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 6, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/about')} sx={{ color: '#1f2937', cursor: 'pointer', fontSize: '15px', fontWeight: 500, '&:hover': { color: '#2563eb' }, transition: 'color 0.2s' }}>
            关于 Xup
          </Typography>
          <Typography onClick={() => navigate('/login')} sx={{ color: '#1f2937', cursor: 'pointer', fontSize: '15px', fontWeight: 400, '&:hover': { color: '#000000' }, transition: 'color 0.2s' }}>
            登录
          </Typography>
          <Button
            onClick={() => navigate('/login')}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6, #2563eb) padding-box, linear-gradient(90deg, #FF0080, #00FFFF, #00FF00, #FFD700, #FF0080) border-box',
              border: '3px solid transparent',
              color: 'white',
              borderRadius: '100px',
              padding: '3px 24px',
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
              backgroundSize: '200% 100%',
              backgroundPosition: '0% 50%',
              transition: 'background-position 2s linear, box-shadow 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb, #1d4ed8) padding-box, linear-gradient(90deg, #FF0080, #00FFFF, #00FF00, #FFD700, #FF0080) border-box',
                backgroundSize: '200% 100%',
                boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)',
                backgroundPosition: '100% 50%',
              },
            }}
          >
            立即开始创作
          </Button>
        </Box>
      </Box>

      {/* Hero Section */}
      <Container
        maxWidth="lg"
        sx={{
          flex: 1,
          minHeight: { xs: 'auto', md: 'calc(100vh - 96px)' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          position: 'relative',
          zIndex: 10,
          py: { xs: 10, md: 0 },
        }}
      >
        {/* 左侧装饰图 */}
        <Box
          component="img"
          src="/change.svg"
          alt="Xup AI 创作平台 · 机器人与人类协作示意"
          sx={{
            position: 'absolute',
            left: { xs: '5%', md: '15%' },
            top: { xs: '15%', md: '15%' },
            width: { xs: '200px', md: '500px' },
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />

        {/* 右侧文字区域 */}
        <Box
          sx={{
            position: 'absolute',
             // Adjust left position to be to the right of the image (15% + width approx)
             // Image is 500px wide on desktop.
            left: { xs: '50%', md: '50%' },
            top: { xs: '60%', md: '25%' },
            transform: { xs: 'translateX(-50%)', md: 'none' },
            textAlign: { xs: 'center', md: 'left' },
            zIndex: 11,
          }}
        >
          <TypewriterText
            segments={[
              { text: '欢迎来到' },
              { text: 'Ai时代', accent: true },
              { text: '！' },
            ]}
          />
          <Typography
            sx={{
              fontSize: { xs: '1rem', md: '1.5rem' },
              fontWeight: 500,
              lineHeight: 1.6,
              color: '#404040',
            }}
          >
            机器人?世界模型？AGI?AI安全！
          </Typography>

          {/* 中央登录 / 注册按钮 —— 玻璃拟态 + 流光渐变描边 */}
          <Box
            sx={{
              mt: { xs: 3, md: 4 },
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-start' },
              opacity: 0,
              animation: `${authBtnFadeIn} 0.9s ease-out 1.6s forwards`,
            }}
          >
            <Box
              onClick={() => navigate('/login')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/login'); }}
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                px: { xs: 3.5, md: 4.5 },
                py: { xs: 1.25, md: 1.6 },
                borderRadius: '999px',
                cursor: 'pointer',
                userSelect: 'none',
                fontFamily: SERIF_STACK,
                fontSize: { xs: '15px', md: '17px' },
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: '#0f172a',
                background: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(14px) saturate(160%)',
                WebkitBackdropFilter: 'blur(14px) saturate(160%)',
                boxShadow:
                  '0 10px 30px rgba(37, 99, 235, 0.15), inset 0 1px 0 rgba(255,255,255,0.9)',
                transition:
                  'transform 0.35s cubic-bezier(0.2,0.8,0.2,1), box-shadow 0.35s ease, color 0.25s ease',
                animation: `${authBtnFloat} 4.5s ease-in-out infinite`,
                // 渐变描边（padding-box / border-box 双背景叠加）
                border: '1.5px solid transparent',
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.65), rgba(255,255,255,0.45)), linear-gradient(120deg, #60a5fa 0%, #a78bfa 40%, #f472b6 70%, #60a5fa 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                backgroundSize: '100% 100%, 300% 100%',
                backgroundPosition: '0 0, 0% 50%',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  color: '#1e3a8a',
                  boxShadow:
                    '0 18px 40px rgba(37, 99, 235, 0.28), inset 0 1px 0 rgba(255,255,255,1)',
                  animation: `${authBtnShine} 3.5s linear infinite`,
                },
                '&:hover .auth-btn-arrow': {
                  transform: 'translateX(4px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 6px 18px rgba(37, 99, 235, 0.2)',
                },
                '&:focus-visible': {
                  outline: 'none',
                  boxShadow:
                    '0 0 0 3px rgba(96, 165, 250, 0.45), 0 10px 30px rgba(37, 99, 235, 0.2)',
                },
              }}
            >
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>登录</Box>
                <Box component="span" sx={{ opacity: 0.45, fontWeight: 400 }}>/</Box>
                <Box
                  component="span"
                  sx={{
                    fontStyle: 'italic',
                    fontWeight: 500,
                    background: 'linear-gradient(90deg, #2563eb, #7c3aed)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Register
                </Box>
              </Box>
              <ArrowForwardIcon
                className="auth-btn-arrow"
                sx={{
                  fontSize: 18,
                  transition: 'transform 0.35s cubic-bezier(0.2,0.8,0.2,1)',
                  color: '#2563eb',
                }}
              />
            </Box>
          </Box>
        </Box>
        {/* 向下滚动提示 —— 锚到 Hero 底部并下沉,首屏只露出 "Scroll · 向下探索更多" 文字,
            竖线和圆按钮藏在 fold 下面作为 teaser,鼓励用户滚动探索 */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: { xs: -60, md: -72 },
            zIndex: 12,
            display: 'flex',
            justifyContent: 'center',
            opacity: 0,
            animation: `${authBtnFadeIn} 1s ease-out 2.2s forwards`,
          }}
        >
        <Box
          onClick={() => {
            window.scrollTo({ top: window.innerHeight * 0.95, behavior: 'smooth' });
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              window.scrollTo({ top: window.innerHeight * 0.95, behavior: 'smooth' });
            }
          }}
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.25,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover .scroll-dot': {
              transform: 'translateY(8px)',
            },
            '&:hover .scroll-text': {
              color: '#1d4ed8',
              letterSpacing: '0.32em',
            },
          }}
        >
          {/* 文本：小字 uppercase tracked */}
          <Box
            className="scroll-text"
            sx={{
              fontSize: 11,
              letterSpacing: '0.28em',
              fontWeight: 600,
              color: '#64748b',
              textTransform: 'uppercase',
              transition: 'color 0.35s ease, letter-spacing 0.45s ease',
            }}
          >
            Scroll · 向下探索更多
          </Box>

          {/* 竖向细线 + 下滑点 */}
          <Box
            sx={{
              position: 'relative',
              width: 1,
              height: 48,
              background:
                'linear-gradient(180deg, rgba(148,163,184,0) 0%, rgba(59,130,246,0.55) 50%, rgba(148,163,184,0) 100%)',
              overflow: 'visible',
            }}
          >
            <Box
              className="scroll-dot"
              sx={{
                position: 'absolute',
                top: 6,
                left: '50%',
                width: 6,
                height: 6,
                marginLeft: '-3px',
                borderRadius: '50%',
                bgcolor: '#2563eb',
                boxShadow: '0 0 10px rgba(37, 99, 235, 0.55)',
                animation: `${scrollBounce} 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
                transition: 'transform 0.4s cubic-bezier(0.2,0.8,0.2,1)',
              }}
            />
          </Box>

          {/* 双 chevron */}
          <Box
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: -0.5,
              animation: `${scrollGlow} 2.4s ease-in-out infinite`,
              borderRadius: '50%',
              width: 36,
              height: 36,
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 24 24"
              sx={{
                width: 16,
                height: 16,
                fill: 'none',
                stroke: '#2563eb',
                strokeWidth: 2.2,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                animation: `${scrollBounce} 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
              }}
            >
              <polyline points="6 9 12 15 18 9" />
            </Box>
          </Box>
        </Box>
        </Box>
      </Container>

      {/* 精选能力 · 宣传卡片组 —— 大厂极简风 */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          zIndex: 10,
          mt: { xs: 2, md: 4 },
          pt: { xs: 8, md: 14 },
          pb: { xs: 8, md: 14 },
          px: { xs: 3, md: 6 },
          background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 18%, #ffffff 100%)',
        }}
      >
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          {/* 区域标题 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  letterSpacing: '0.24em',
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  mb: 1.25,
                }}
              >
                Featured · 精选能力
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: 26, sm: 32, md: 40 },
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: '#0f172a',
                  lineHeight: 1.15,
                  fontFamily: SERIF_STACK,
                }}
              >
                把最前沿的 AI，带到你的指尖
              </Typography>
              <Typography sx={{ mt: 1.5, fontSize: { xs: 14, md: 15 }, color: '#475569', maxWidth: 620 }}>
                四项核心能力，覆盖创作、编辑、资讯全链路 · 每日更新
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"SF Mono", "Roboto Mono", ui-monospace, monospace',
                fontSize: 12,
                color: '#94a3b8',
                letterSpacing: '0.1em',
                fontWeight: 500,
                display: { xs: 'none', md: 'block' },
              }}
            >
              04 / CAPABILITIES
            </Typography>
          </Box>

          {/* 卡片网格 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: { xs: 2, md: 2.5 },
            }}
          >
            {[
              {
                number: '01',
                eyebrow: '免费 · 优选',
                title: '豆包 AI 去水印',
                desc: '抖音 / 小红书 / 微博 视频与图集，一键剥离水印，无损原画质。',
                tag: '公益渠道',
                accent: '#10b981',
                Icon: AutoFixHighIcon,
                to: '/doubao',
              },
              {
                number: '02',
                eyebrow: 'NEW · 上新',
                title: 'GPT Image 2',
                desc: 'OpenAI 新一代图像模型，14 种比例 · 支持参考图，细节与语义兼得。',
                tag: '已上线',
                accent: '#6366f1',
                Icon: PhotoFilterIcon,
                to: '/home?m=gpt',
              },
              {
                number: '03',
                eyebrow: 'LIMITED · 优惠',
                title: 'Grok Imagine 30s',
                desc: '一次生成 30 秒高清长镜头，原生音效与镜头语言一气呵成。',
                tag: '优惠渠道',
                accent: '#f59e0b',
                Icon: BoltIcon,
                to: '/home?m=grok30',
              },
              {
                number: '04',
                eyebrow: 'DAILY · 日更',
                title: '守卫导航',
                desc: '精选全球 AI 资讯，每日早 8 点更新，把握前沿不错过任何节点。',
                tag: '每日推送',
                accent: '#0ea5e9',
                Icon: NewspaperIcon,
                to: '/guard',
              },
            ].map((card) => (
              <Box
                key={card.number}
                onClick={() => navigate(card.to)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(card.to); }}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: { xs: 220, md: 260 },
                  p: { xs: 2.75, md: 3.25 },
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  transition:
                    'transform 0.35s cubic-bezier(0.2,0.8,0.2,1), box-shadow 0.35s ease, border-color 0.35s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 24px 48px -18px ${card.accent}40, 0 8px 16px -8px rgba(15,23,42,0.08)`,
                    borderColor: `${card.accent}66`,
                  },
                  '&:hover .promo-arrow': {
                    transform: 'translate(4px, -4px)',
                    bgcolor: card.accent,
                    color: '#fff',
                    borderColor: card.accent,
                  },
                  '&:hover .promo-glow': {
                    opacity: 1,
                    transform: 'scale(1.15)',
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    boxShadow: `0 0 0 3px ${card.accent}55`,
                  },
                }}
              >
                {/* 左上角彩色光晕 */}
                <Box
                  className="promo-glow"
                  sx={{
                    position: 'absolute',
                    top: -60,
                    left: -60,
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${card.accent}33 0%, ${card.accent}00 70%)`,
                    opacity: 0.55,
                    transition: 'opacity 0.45s ease, transform 0.6s ease',
                    pointerEvents: 'none',
                  }}
                />

                {/* 顶部：编号 + tag */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: '"SF Mono", "Roboto Mono", ui-monospace, monospace',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#94a3b8',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {card.number}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      color: card.accent,
                      bgcolor: `${card.accent}14`,
                      px: 1,
                      py: 0.35,
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {card.tag}
                  </Box>
                </Box>

                {/* 中部：图标 + 文案 */}
                <Box sx={{ position: 'relative', zIndex: 1, mt: 2 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${card.accent}14`,
                      color: card.accent,
                      mb: 1.75,
                    }}
                  >
                    <card.Icon sx={{ fontSize: 22 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      letterSpacing: '0.18em',
                      fontWeight: 600,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      mb: 0.75,
                    }}
                  >
                    {card.eyebrow}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 18, md: 20 },
                      fontWeight: 700,
                      color: '#0f172a',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.25,
                      mb: 1,
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: '#475569',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {card.desc}
                  </Typography>
                </Box>

                {/* 底部：箭头按钮 */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5, position: 'relative', zIndex: 1 }}>
                  <Box
                    className="promo-arrow"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #e5e7eb',
                      color: '#0f172a',
                      bgcolor: '#fff',
                      transition:
                        'transform 0.35s cubic-bezier(0.2,0.8,0.2,1), background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
                    }}
                  >
                    <ArrowOutwardIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <SunAnimation />
    </Box>
  );
};

export default LandingPage;
